package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlin.time.Duration.Companion.minutes
import kotlin.time.Instant
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

/**
 * A business's open/closed state at a moment in time, evaluated in the business's own local time.
 *
 * [transition] is the local time the status next flips: the closing time when [isOpen], otherwise the
 * next opening time. [transitionDay] is null when that flip happens later the same day; otherwise it's
 * the upcoming day the business opens (so the UI can show "Opens Mon 9:00 AM"). The UI formats these
 * into localized strings — this stays free of any display copy.
 */
data class OpenState(
    val isOpen: Boolean,
    val transition: LocalTime,
    val transitionDay: DayOfWeek?,
)

private const val MINUTES_PER_DAY = 24 * 60
private const val MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY

// Business.hours keys: lowercase 3-letter weekday (Monday first, matching DayOfWeek ordering).
private val DAY_KEYS = listOf("mon", "tue", "wed", "thu", "fri", "sat", "sun")

/** "HH:MM" (24-hour) → minutes past midnight, or null if malformed / out of range. */
private fun parseTime(value: String): Int? {
    val parts = value.trim().split(":")
    if (parts.size != 2) return null
    val h = parts[0].toIntOrNull() ?: return null
    val m = parts[1].toIntOrNull() ?: return null
    if (h !in 0..23 || m !in 0..59) return null
    return h * 60 + m
}

/**
 * Flatten a `Business.hours` map into absolute minute-of-week intervals `[start, end)` with Monday 00:00
 * as minute 0. A range whose end is ≤ its start is treated as overnight (end += one day), so a Friday
 * 18:00–02:00 shift becomes Fri-18:00 → Sat-02:00. Values are comma-separated "HH:MM-HH:MM" ranges; an
 * empty value or "closed" (or a missing day) means closed that day. Malformed pieces are skipped.
 */
private fun intervals(hours: Map<String, String>): List<IntRange> {
    val out = mutableListOf<IntRange>()
    DAY_KEYS.forEachIndexed { dayIndex, key ->
        val spec = hours[key]?.trim()?.lowercase() ?: return@forEachIndexed
        if (spec.isEmpty() || spec == "closed") return@forEachIndexed
        val base = dayIndex * MINUTES_PER_DAY
        spec.split(",").forEach { range ->
            val ends = range.split("-")
            if (ends.size != 2) return@forEach
            val start = parseTime(ends[0]) ?: return@forEach
            var end = parseTime(ends[1]) ?: return@forEach
            if (end <= start) end += MINUTES_PER_DAY // overnight
            out.add(base + start until base + end)
        }
    }
    return out
}

/** The nth (1-based) [weekday]-of-[month] in [year], e.g. the 2nd Sunday of March. */
private fun nthWeekday(year: Int, month: Month, weekday: DayOfWeek, n: Int): LocalDate {
    val first = LocalDate(year, month, 1)
    val offsetToFirst = (weekday.isoDayNumber - first.dayOfWeek.isoDayNumber + 7) % 7
    return LocalDate(year, month, 1 + offsetToFirst + (n - 1) * 7)
}

/**
 * Total UTC offset (minutes) for a Canadian metro with the given winter [standardOffsetHours] at
 * [instant], accounting for North American DST: 2nd Sunday of March 02:00 (local standard) → 1st Sunday
 * of November 02:00 (local daylight). Uses only [TimeZone.UTC], so it works identically on every
 * platform (incl. Kotlin/JS, whose bundle carries no IANA timezone database). All app metros observe DST.
 */
fun canadianOffsetMinutes(standardOffsetHours: Int, instant: Instant): Int {
    val year = instant.toLocalDateTime(TimeZone.UTC).year
    // Spring forward at 02:00 local *standard* time; local = UTC + standardOffset, so UTC = local − offset.
    val springForward = LocalDateTime(nthWeekday(year, Month.MARCH, DayOfWeek.SUNDAY, 2), LocalTime(2, 0))
        .toInstant(TimeZone.UTC).plus((-standardOffsetHours * 60).minutes)
    // Fall back at 02:00 local *daylight* time (offset = standard + 1).
    val fallBack = LocalDateTime(nthWeekday(year, Month.NOVEMBER, DayOfWeek.SUNDAY, 1), LocalTime(2, 0))
        .toInstant(TimeZone.UTC).plus((-(standardOffsetHours + 1) * 60).minutes)
    val isDst = instant >= springForward && instant < fallBack
    return (standardOffsetHours + if (isDst) 1 else 0) * 60
}

/**
 * The [OpenState] for [hours] at [now], where local time is UTC shifted by [offsetMinutes] (typically
 * from [canadianOffsetMinutes]). Returns null when the business has no usable hours, so the UI shows
 * nothing rather than a guessed status. Handles overnight ranges, split shifts, closed days, and the
 * week wrap (Sunday-night → Monday-morning).
 */
fun openStateAt(hours: Map<String, String>, offsetMinutes: Int, now: Instant): OpenState? {
    val intervals = intervals(hours)
    if (intervals.isEmpty()) return null

    val local = now.plus(offsetMinutes.minutes).toLocalDateTime(TimeZone.UTC)
    val nowMinute = (local.dayOfWeek.isoDayNumber - 1) * MINUTES_PER_DAY + local.hour * 60 + local.minute

    // Open? now (or now shifted a week forward, to catch Sunday-overnight intervals) inside a range.
    intervals.firstOrNull { nowMinute in it || (nowMinute + MINUTES_PER_WEEK) in it }?.let { open ->
        return OpenState(isOpen = true, transition = minuteToLocalTime(open.last + 1), transitionDay = null)
    }

    // Closed: soonest upcoming opening, wrapping into next week if needed.
    val nextOpen = intervals.minOf { if (it.first >= nowMinute) it.first else it.first + MINUTES_PER_WEEK }
    val opensLaterToday = nextOpen < (nowMinute / MINUTES_PER_DAY + 1) * MINUTES_PER_DAY
    val day = if (opensLaterToday) null else DayOfWeek.entries[(nextOpen / MINUTES_PER_DAY) % 7]
    return OpenState(isOpen = false, transition = minuteToLocalTime(nextOpen), transitionDay = day)
}

private fun minuteToLocalTime(minuteOfWeek: Int): LocalTime {
    val minuteOfDay = ((minuteOfWeek % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    return LocalTime(hour = minuteOfDay / 60, minute = minuteOfDay % 60)
}

/**
 * The winter/standard UTC offset (hours) the business's hours are expressed in: its metro's, else a
 * per-province fallback (Ontario/Québec → Eastern −5, BC → Pacific −8, Alberta → Mountain −7).
 */
fun Business.standardOffsetHours(): Int =
    Metros.byId(metroId)?.standardUtcOffsetHours ?: when (address.province.uppercase()) {
        "BC" -> -8
        "AB" -> -7
        else -> -5
    }

/** [OpenState] for this business at [now], or null when it has no usable hours. */
fun Business.openStateAt(now: Instant): OpenState? =
    openStateAt(hours, canadianOffsetMinutes(standardOffsetHours(), now), now)
